<script>
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import DashboardLayout from "../layouts/DashboardLayout.vue";
import StateBlock from "../components/StateBlock.vue";
import {api} from "../services/api";

export default {
    name: "VisitorsView",
    components: {
        VueSelect,
        DashboardLayout,
        StateBlock,
    },
    data() {
        return {
            visitors: [],
            loading: true,
            page: 1,
            pageSize: 20,
            pageSizeOptions: [
                {label: "10 / page", value: 10},
                {label: "20 / page", value: 20},
                {label: "50 / page", value: 50},
                {label: "100 / page", value: 100},
            ],
            totalCount: 0,
            totalPages: 0,
            errorMessage: "",
        };
    },
    methods: {
        async loadVisitors() {
            this.loading = true;
            this.errorMessage = "";
            try {
                const response = await api.get("/visitors", {
                    params: {
                        page: this.page,
                        pageSize: this.pageSize,
                    },
                });

                const payload = response.data || {};
                this.visitors = payload.items ?? payload.Items ?? [];
                this.page = payload.page ?? payload.Page ?? this.page;
                this.pageSize = payload.pageSize ?? payload.PageSize ?? this.pageSize;
                this.totalCount = payload.totalCount ?? payload.TotalCount ?? 0;
                this.totalPages = payload.totalPages ?? payload.TotalPages ?? 0;
            } catch (error) {
                this.visitors = [];
                this.totalCount = 0;
                this.totalPages = 0;
                this.errorMessage = error?.response?.data?.error || "Visitors could not be loaded.";
            } finally {
                this.loading = false;
            }
        },
        async goToPage(nextPage) {
            if (nextPage < 1 || (this.totalPages && nextPage > this.totalPages)) return;
            this.page = nextPage;
            await this.loadVisitors();
        },
        async changePageSize() {
            this.page = 1;
            await this.loadVisitors();
        },
        async deleteVisitor(visitor) {
            if (!confirm(`Delete visitor "${visitor.name || visitor.email || visitor.id}"?`)) return;
            try {
                await api.delete(`/visitors/${visitor.id}`);
                if (this.visitors.length === 1 && this.page > 1) {
                    this.page -= 1;
                }
                await this.loadVisitors();
            } catch (error) {
                this.errorMessage = error?.response?.data?.error || "Visitor could not be deleted.";
            }
        },
        preferredCountries(visitor) {
            return visitor.preferredCountries
                ?.map((country) => country.name || country.id)
                .filter(Boolean)
                .join(", ");
        },
        visitorDetails(visitor) {
            return [
                visitor.duration ? `Duration: ${visitor.duration}` : "",
                visitor.visitorCountry ? `Country: ${visitor.visitorCountry}` : "",
                visitor.academicLevel ? `Level: ${visitor.academicLevel}` : "",
                visitor.academicDiscipline ? `Discipline: ${visitor.academicDiscipline}` : "",
                this.additionalFields(visitor),
            ]
                .filter(Boolean)
                .join(" · ");
        },
        additionalFields(visitor) {
            if (!visitor.additionalFieldsJson) return "";
            try {
                return Object.entries(JSON.parse(visitor.additionalFieldsJson))
                    .map(([key, value]) => `${key}: ${value}`)
                    .join(" · ");
            } catch {
                return "";
            }
        },
        formatDate(value) {
            return value ? new Date(value).toLocaleString() : "-";
        },
    },
    mounted() {
        this.loadVisitors();
    },
};
</script>
<template>
    <DashboardLayout title="Visitors" subtitle="Browse widget visitors and their latest activity.">
        <section class="panel">
            <div class="toolbar">
                <h2>Visitors</h2>
                <div style="display: flex; gap: 12px; align-items: center">
                    <VueSelect
                        v-model="pageSize"
                        :options="pageSizeOptions"
                        label="label"
                        :clearable="false"
                        :searchable="false"
                        :reduce="(option) => option.value"
                        @update:modelValue="changePageSize"
                    />
                    <button class="ghost-button" :disabled="loading" @click="loadVisitors">Refresh</button>
                </div>
            </div>

            <div v-if="errorMessage" class="error-banner">{{ errorMessage }}</div>

            <StateBlock v-if="!loading && visitors.length === 0" title="No visitors yet" message="Widget visitors will appear here after conversations start." />

            <div v-else class="table-container">
                <table class="table">
                    <thead>
                        <tr>
                            <th>Visitor</th>
                            <th>Contact</th>
                            <th>Agent</th>
                            <th>Interest</th>
                            <th>Sessions</th>
                            <th>Last Activity</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td colspan="7" style="color: var(--text-secondary)">Loading visitors...</td>
                        </tr>
                        <tr v-for="visitor in visitors" v-else :key="visitor.id">
                            <td>
                                <div style="font-weight: 600">{{ visitor.name || "Unknown visitor" }}</div>
                                <div style="color: var(--text-secondary); font-size: 12px">{{ visitor.isLead ? "Lead" : "Visitor" }}</div>
                            </td>
                            <td>
                                <div>{{ visitor.email || "-" }}</div>
                                <div style="color: var(--text-secondary); font-size: 12px">{{ visitor.phone || "-" }}</div>
                            </td>
                            <td>{{ visitor.aiAgentName || visitor.aiAgentId }}</td>
                            <td>
                                <div>{{ preferredCountries(visitor) || "-" }}</div>
                                <div style="color: var(--text-secondary); font-size: 12px">{{ visitor.programType || "-" }}{{ visitor.startDate ? " - " + visitor.startDate : "" }}</div>
                                <div v-if="visitorDetails(visitor)" style="color: var(--text-secondary); font-size: 12px">{{ visitorDetails(visitor) }}</div>
                            </td>
                            <td>{{ visitor.chatSessionCount }}</td>
                            <td>{{ formatDate(visitor.lastMessageAt || visitor.updatedAt || visitor.createdAt) }}</td>
                            <td><button class="ghost-button" @click="deleteVisitor(visitor)">Delete</button></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-top: 18px">
                <span style="color: var(--text-secondary); font-size: 13px">
                    {{ totalCount }} visitors
                    <span v-if="totalPages"> - page {{ page }} of {{ totalPages }}</span>
                </span>
                <div style="display: flex; gap: 8px">
                    <button class="ghost-button" :disabled="loading || page <= 1" @click="goToPage(page - 1)">Previous</button>
                    <button class="ghost-button" :disabled="loading || page >= totalPages" @click="goToPage(page + 1)">Next</button>
                </div>
            </div>
        </section>
    </DashboardLayout>
</template>

<style scoped>
.error-banner {
    margin-bottom: 12px;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid color-mix(in srgb, #ef4444 45%, transparent);
    background: color-mix(in srgb, #ef4444 12%, transparent);
    color: #b91c1c;
    font-size: 0.9rem;
}
</style>
